import type {DefaultMantineColor} from '@mantine/core';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {
  UserActivitySummaryResponse,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  UserStatePills,
  UserTypePills,
} from 'src/shared/constants/PillConstants';
import {
  UserStateDataOptions,
  UserTypeDataOptions,
} from 'src/shared/constants/columnFilterData';
import {
  formatDate,
  formatDayRelativeOrNever,
  getDaysDiff,
} from 'src/shared/utils/formatters';

const getLastActivePill = (
  lastActiveAt: Date | undefined
): {text: string; color: DefaultMantineColor} => {
  const text = formatDayRelativeOrNever(lastActiveAt);

  if (!lastActiveAt) return {text, color: 'gray'};

  const diffDays = getDaysDiff(new Date(lastActiveAt));

  let color: DefaultMantineColor;
  if (diffDays < 1) color = 'green';
  else if (diffDays < 7) color = 'yellow';
  else color = 'red';

  return {text, color};
};

export const useUserActivityColumns = () => {
  return useMemo<MRT_ColumnDef<UserActivitySummaryResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableColumnFilterModes: false,
      },
      {
        id: 'lastActiveAt',
        accessorFn: row => row.lastActiveAt,
        header: 'Last Active',
        enableColumnFilter: false,
        Cell: ({row}) => {
          const pill = getLastActivePill(row.original.lastActiveAt);
          return <Pill {...pill} />;
        },
      },
      {
        accessorKey: 'daysActiveInPeriod',
        header: 'Days Active (30d)',
        enableColumnFilter: false,
      },
      {
        id: 'lastActiveAtDate',
        accessorFn: row =>
          row.lastActiveAt ? new Date(row.lastActiveAt) : null,
        header: 'Last Active At',
        enableColumnFilter: false,
        Cell: ({row}) => <>{formatDate(row.original.lastActiveAt)}</>,
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
        enableColumnFilterModes: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableColumnFilterModes: false,
      },
      {
        accessorKey: 'userType',
        header: 'User Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserTypePills(cell.getValue<UserTypeEnum>())} />
        ),
      },
      {
        accessorKey: 'active',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserStatePills(cell.getValue<UserStatusEnum>())} />
        ),
      },
    ],
    []
  );
};
